import {
  PURCHASE_STAGES,
  PURCHASE_TYPES,
  type PurchaseQueueIndicator,
  type PurchaseStage,
  type PurchaseType,
} from '@loot/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import {
  FrontTitle,
  IndicatorCard,
  IndicatorFailure,
  leaveFocus,
  PendingIndicator,
} from '../../components/home-cards.tsx';
import { Badge, Button, ErrorBox, Loading, PageHeader } from '../../components/ui.tsx';

/** Prazo de um grupo: atendimento ou aprovação (nunca somados entre si). */
interface DeadlineIndicator {
  state: 'ok' | 'rule_inactive';
  inside: number;
  outside: number;
  none: number;
  total?: number;
}
interface CountIndicator {
  state: 'ok';
  count: number;
  /** Conta o grupo todo, mesmo com uma empresa em foco (ADR 0100). */
  groupWide?: boolean;
}
interface DocumentsIndicator extends CountIndicator {
  suppliers: number;
  bySituation: Record<string, number>;
}
/** Compras em processamento (ADR 0102): total, por tipo e por etapa. */
interface ProcessingIndicator {
  state: 'ok';
  total: number;
  byType: Record<PurchaseType, number>;
  byStage: Record<PurchaseStage, number>;
}
type Failed = { state: 'error' };

/** Nulo: a pessoa não abre a lista daquele indicador, e o cartão não aparece. */
interface HomeIndicators {
  attendance: DeadlineIndicator | Failed | null;
  approval: DeadlineIndicator | Failed | null;
  awaitingReply: CountIndicator | Failed | null;
  processing: ProcessingIndicator | Failed | null;
  documents: DocumentsIndicator | Failed | null;
  homologation: CountIndicator | Failed | null;
}

const DOCUMENT_ORDER = ['missing', 'requested', 'in_review', 'rejected', 'expired'];

/** Sempre refeito ao abrir: depois de uma correção, o número volta atualizado. */
function useHomeIndicators() {
  return useQuery({
    queryKey: ['purchasing', 'home-indicators'],
    queryFn: () => api.get<HomeIndicators>('/purchasing/home-indicators'),
    refetchOnMount: 'always',
  });
}

const failed = (v: object | null): v is Failed => !!v && 'state' in v && v.state === 'error';

/** Número que abre a lista filtrada: o que está na lista é o que foi contado. */
function CountLink({
  indicator,
  value,
  label,
  late = false,
}: {
  indicator: PurchaseQueueIndicator;
  value: number;
  label: string;
  late?: boolean;
}) {
  const { i18n } = useTranslation();
  return (
    <Link
      to="/purchasing/queue"
      search={{ indicator } as never}
      className="flex min-w-0 flex-1 flex-col rounded-md border border-rule px-3 py-2 hover:border-control-border hover:bg-paper"
    >
      <span
        className={`text-2xl font-semibold tabular-nums ${late && value > 0 ? 'text-danger' : ''}`}
      >
        {value.toLocaleString(i18n.language)}
      </span>
      <span className="text-xs text-muted">{label}</span>
    </Link>
  );
}

function DeadlineCard({
  group,
  data,
  onRetry,
}: {
  group: 'attendance' | 'approval';
  data: DeadlineIndicator | Failed;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const title = t(`purchasing.home.${group}.title`);
  if (failed(data)) return <IndicatorFailure title={title} onRetry={onRetry} />;
  const showNumbers = data.state === 'ok';
  const total = data.total ?? 0;
  // Sem nada para tratar: continua à vista, mas ocupa pouco, como os prazos das outras frentes.
  if (showNumbers && data.inside === 0 && data.outside === 0 && data.none === 0) {
    const criterion = t(`purchasing.home.${group}.criterion`);
    const quiet = 'text-muted hover:text-ink hover:underline';
    return (
      <IndicatorCard>
        <span className="text-sm font-medium">{title}</span>
        <span className="flex flex-wrap gap-x-3 text-xs">
          <Link
            to="/purchasing/queue"
            search={{ indicator: `${group}_in` } as never}
            className={quiet}
          >
            0 {t('purchasing.home.inside')}
          </Link>
          <Link
            to="/purchasing/queue"
            search={{ indicator: `${group}_out` } as never}
            className={quiet}
          >
            0 {t('purchasing.home.outside')}
          </Link>
        </span>
        <span title={criterion} className="truncate text-xs text-muted">
          {criterion}
        </span>
      </IndicatorCard>
    );
  }
  return (
    <IndicatorCard>
      <span className="text-sm font-medium">{title}</span>
      {data.state === 'rule_inactive' && <Badge>{t('purchasing.home.ruleInactive')}</Badge>}
      {showNumbers && (
        <div className="flex gap-2">
          <CountLink
            indicator={`${group}_in`}
            value={data.inside}
            label={t('purchasing.home.inside')}
          />
          <CountLink
            indicator={`${group}_out`}
            value={data.outside}
            label={t('purchasing.home.outside')}
            late
          />
        </div>
      )}
      {showNumbers && data.none > 0 && (
        <Link
          to="/purchasing/queue"
          search={{ indicator: `${group}_none` } as never}
          className="text-xs text-brand-text hover:underline"
        >
          {t('purchasing.home.noDeadline', { count: data.none })}
        </Link>
      )}
      {!showNumbers && group === 'approval' && total > 0 && (
        <Link
          to="/purchasing/queue"
          search={{ indicator: 'approval_all' } as never}
          className="text-xs text-brand-text hover:underline"
        >
          {t('purchasing.home.approval.waiting', { count: total })}
        </Link>
      )}
      <span className="mt-auto border-t border-dashed border-rule pt-1.5 text-xs text-muted">
        {t(`purchasing.home.${group}.criterion`)}
      </span>
    </IndicatorCard>
  );
}

/**
 * Em processamento: as abertas no total e por tipo e, embaixo, por etapa. Cada número abre a fila
 * com as mesmas compras que contou.
 */
function ProcessingCards({
  data,
  onRetry,
}: {
  data: ProcessingIndicator | Failed;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  if (failed(data)) {
    return <IndicatorFailure title={t('purchasing.home.processing.title')} onRetry={onRetry} />;
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
        <CountLink
          indicator="in_progress"
          value={data.total}
          label={t('purchasing.home.processing.total')}
        />
        {PURCHASE_TYPES.map((type) => (
          <CountLink
            key={type}
            indicator={`type_${type}`}
            value={data.byType[type] ?? 0}
            label={t(`purchasing.requestTypes.${type}`)}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span className="text-muted">{t('purchasing.home.processing.byStage')}</span>
        {PURCHASE_STAGES.map((stage) => (
          <Link
            key={stage}
            to="/purchasing/queue"
            search={{ indicator: `stage_${stage}` } as never}
            className="text-brand-text hover:underline"
          >
            {t(`purchasing.home.stages.${stage}`)}:{' '}
            <span className="tabular-nums">{data.byStage[stage] ?? 0}</span>
          </Link>
        ))}
      </div>
      <span className="text-xs text-muted">{t('purchasing.home.processing.criterion')}</span>
    </div>
  );
}

const openLink = 'text-sm text-brand-text hover:underline';

/** Os cartões de pendência que a pessoa pode ver; com `all`, também os zerados. */
function Pending({
  data,
  all,
  onRetry,
}: {
  data: HomeIndicators;
  all: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const cards: ReactNode[] = [];
  const { awaitingReply, documents, homologation } = data;
  if (awaitingReply) {
    const title = t('purchasing.home.awaitingReply.title');
    if (failed(awaitingReply)) {
      cards.push(<IndicatorFailure key="reply" title={title} onRetry={onRetry} />);
    } else if (all || awaitingReply.count > 0) {
      cards.push(
        <PendingIndicator
          key="reply"
          title={title}
          value={awaitingReply.count}
          unit={t('purchasing.home.units.requests', { count: awaitingReply.count })}
          criterion={t('purchasing.home.awaitingReply.criterion')}
          to={
            <Link
              to="/purchasing/queue"
              search={{ indicator: 'awaiting_reply' } as never}
              className={openLink}
            >
              {t('purchasing.home.open')}
            </Link>
          }
        />,
      );
    }
  }
  if (documents) {
    const title = t('purchasing.home.documents.title');
    if (failed(documents)) {
      cards.push(<IndicatorFailure key="docs" title={title} onRetry={onRetry} />);
    } else if (all || documents.count > 0) {
      cards.push(
        <PendingIndicator
          key="docs"
          attention
          title={title}
          value={documents.count}
          unit={t('purchasing.home.documents.unit', {
            count: documents.count,
            suppliers: documents.suppliers,
          })}
          detail={
            <span className="flex flex-wrap gap-1">
              {DOCUMENT_ORDER.filter((s) => documents.bySituation[s]).map((s) => (
                <Badge key={s}>
                  {t(`partners.documents.situations.${s}`)}: {documents.bySituation[s]}
                </Badge>
              ))}
            </span>
          }
          criterion={t('purchasing.home.documents.criterion')}
          to={
            <Link
              to="/partners/documents"
              search={{ filter: 'required' } as never}
              className={openLink}
            >
              {t('purchasing.home.open')}
            </Link>
          }
        />,
      );
    }
  }
  if (homologation) {
    const title = t('purchasing.home.homologation.title');
    if (failed(homologation)) {
      cards.push(<IndicatorFailure key="homologation" title={title} onRetry={onRetry} />);
    } else if (all || homologation.count > 0) {
      cards.push(
        <PendingIndicator
          key="homologation"
          groupWide={homologation.groupWide}
          title={title}
          value={homologation.count}
          unit={t('purchasing.home.units.suppliers', { count: homologation.count })}
          criterion={t('purchasing.home.homologation.criterion')}
          to={
            <Link
              to="/partners"
              search={{ status: 'pending' } as never}
              className={openLink}
              onClick={homologation.groupWide ? leaveFocus : undefined}
            >
              {t('purchasing.home.open')}
            </Link>
          }
        />,
      );
    }
  }
  if (cards.length === 0) {
    return <p className="text-sm text-ok">{t('purchasing.home.nothingPending')}</p>;
  }
  return <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{cards}</div>;
}

function Blocks({ all, title }: { all: boolean; title?: string }) {
  const { t } = useTranslation();
  const Heading = title ? 'h3' : 'h2';
  const query = useHomeIndicators();
  const retry = () => void query.refetch();
  if (query.isPending) return <Loading />;
  if (query.isError) {
    return (
      <>
        <FrontTitle title={title} />
        <div className="flex flex-col gap-2">
          <ErrorBox error={query.error} />
          <div>
            <Button variant="secondary" onClick={retry}>
              {t('purchasing.home.retry')}
            </Button>
          </div>
        </div>
      </>
    );
  }
  const data = query.data;
  const hasDeadlines = !!data.attendance || !!data.approval;
  const hasPending = !!data.awaitingReply || !!data.documents || !!data.homologation;
  if (!hasDeadlines && !hasPending && !data.processing) return null;
  return (
    <>
      <FrontTitle title={title} />
      {data.processing && (
        <section className="flex flex-col gap-2" aria-label={t('purchasing.home.processing.title')}>
          <Heading className="text-base font-semibold">
            {t('purchasing.home.processing.title')}
          </Heading>
          <ProcessingCards data={data.processing} onRetry={retry} />
        </section>
      )}
      {hasDeadlines && (
        <section className="flex flex-col gap-2" aria-label={t('purchasing.home.deadlines')}>
          <div className="flex flex-wrap items-baseline gap-x-3">
            <Heading className="text-base font-semibold">{t('purchasing.home.deadlines')}</Heading>
            <p className="text-sm text-muted">{t('purchasing.home.deadlinesHint')}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {data.attendance && (
              <DeadlineCard group="attendance" data={data.attendance} onRetry={retry} />
            )}
            {data.approval && (
              <DeadlineCard group="approval" data={data.approval} onRetry={retry} />
            )}
          </div>
        </section>
      )}
      {hasPending && (
        <section className="flex flex-col gap-2" aria-label={t('purchasing.home.pending')}>
          <div className="flex flex-wrap items-baseline gap-x-3">
            <Heading className="text-base font-semibold">{t('purchasing.home.pending')}</Heading>
            {!all && (
              <Link to="/purchasing/pending" className="text-sm text-brand-text hover:underline">
                {t('purchasing.home.consult')}
              </Link>
            )}
          </div>
          <Pending data={data} all={all} onRetry={retry} />
        </section>
      )}
    </>
  );
}

/** Faixa de indicadores da Visão geral de Compras, acima dos atalhos (ADR 0095). */
export function PurchaseHomeIndicators({ title }: { title?: string }) {
  return <Blocks all={false} title={title} />;
}

/** Consultar pendências: sempre disponível e com todos os indicadores, mesmo os zerados. */
export function PurchasePendingPage() {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader
        title={t('purchasing.home.pendingPage.title')}
        description={t('purchasing.home.pendingPage.description')}
      />
      <Blocks all />
    </>
  );
}
